<!-- /test group "Go Live": puts this session's server clock just before the banner's two
     boundaries for the next online session (the one the banner uses: useOnlineSessions), so a
     tester can watch the banner cross each one in real seconds -->
<template>
  <p v-if="!activeSession">No upcoming online session in the program.</p>
  <template v-else>
    <p>
      Next session: <b>{{ sessionLabel }}</b>, starts {{ getLondonText(activeSession.start) }}
      (London).
      <template v-if="!isRegistered">
        You aren't registered for every upcoming online event, so the banner shows Register Now
        instead of the countdown.
      </template>
      <template v-else>Banner now: {{ bannerState }}.</template>
    </p>
    <div v-for="jump in jumps" :key="jump.label" class="jump">
      <button @click="setTarget(jump.target)">{{ jump.label }}</button>
      <span class="target">sets the server to {{ getLondonText(jump.target) }}</span>
      <p class="expect">{{ jump.expect }}</p>
    </div>
  </template>
</template>

<script setup>
import {computed} from "vue";
import {authFetch} from "@/composables/useAuthFetch";
import useOnlineSessions, {WARM_UP_HOURS} from "@/composables/useOnlineSessions";
import useEventRegistration from "@/composables/useEventRegistration";
import {refreshServerClock} from "@/composables/useServerClock";

const {activeSession, upcomingEventKeys, isLive, isWarmUp, year} = useOnlineSessions();
const {isRegisteredOnline} = useEventRegistration(year);

function getLondonText(dateTime) {
  return dateTime.setZone("Europe/London").toFormat("d LLL yyyy, HH:mm:ss");
}

function getJumps(start) {
  const warmUpStart = start.minus({hours: WARM_UP_HOURS});
  return [
    {
      label: "10 Seconds Before the Warm-Up Pill",
      target: warmUpStart.minus({seconds: 10}),
      expect: "Plain countdown; after about 10 seconds, the green pill linking to /live."
    },
    {
      label: "5 Seconds Before Go Live",
      target: start.minus({seconds: 5}),
      expect: "The /live pill; after about 5 seconds, Join Live Now."
    }
  ];
}

async function setTarget(target) {
  const body = JSON.stringify({target: target.toISO()});
  const headers = {"Content-Type": "application/json"};
  await authFetch("/api/v1/user/clock-override", {method: "POST", headers, body});
  await refreshServerClock();
}

// as the banner: the countdown needs every upcoming online event registered (Banner.vue)
const isRegistered = computed(() => {
  const keys = upcomingEventKeys.value;
  return keys.length > 0 && keys.every((key) => isRegisteredOnline(key));
});
const sessionLabel = computed(() => `Online ${activeSession.value.eventKey} ${year}`);
const jumps = computed(() => getJumps(activeSession.value.start));
const bannerState = computed(() => {
  if (isLive.value) return "Join Live Now";
  if (isWarmUp.value) return "the countdown as a green pill linking to /live";
  return "the plain countdown";
});
</script>

<style scoped>
.jump {
  margin-top: 1rem;
}
.target {
  margin-left: 0.5rem;
  font-variant-numeric: tabular-nums;
}
.expect {
  margin: 0.25rem 0 0;
  opacity: 0.8;
}
</style>
